import { Skeleton } from '@tiger/bs-comps';
import { useMount } from '@tiger/hooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ErrorBox from 'components/ErrorBox';
import classList from 'utils/classlist';
import { detectScroll, scrollToTop } from './helper';
import { useGetProvinceData } from './hooks';
import { CityType, ProvinceType, SelectTypeEnum } from './type';

interface ProvinceCascaderProps {
    value: string[];
    onChange: (value: string[], closeOption: boolean) => void;
    onCancel: () => void;
}

const ProvinceCascader: React.FC<ProvinceCascaderProps> = ({ value, onChange, onCancel }) => {
    const { data: dataProvince, isLoading, error, mutate } = useGetProvinceData();
    const provinceList = useMemo<ProvinceType[]>(() => dataProvince?.data || [], [dataProvince]);
    const [cityList, setCityList] = useState<CityType[]>([]);
    const [isTop, setIsTop] = useState<boolean>(false);
    const provinceRef = useRef<HTMLUListElement | null>(null);
    const cityRef = useRef<HTMLUListElement | null>(null);
    const districtRef = useRef<HTMLUListElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement>();
    const districtList = useMemo(() => {
        if (cityList.length > 0 && value[1]) {
            return cityList.find(item => value[1] === item.city)?.district || [];
        }

        return [];
    }, [cityList, value]);

    useEffect(() => {
        if (provinceList.length && value[0]) {
            getCity(provinceList, value[0]);
        }
    }, [provinceList, value]);

    useMount(() => {
        if (wrapperRef.current) {
            const { top = 0 } = wrapperRef.current.getBoundingClientRect() || {};
            const windowHeight = window.innerHeight;
            const maxHeight = 180;

            if (maxHeight + top > windowHeight) {
                setIsTop(true);
            } else if (maxHeight + top < windowHeight) {
                setIsTop(false);
            }
        }
    });

    const getCity = (data: ProvinceType[], value: string) => {
        const province = data.find(item => item.province === value);

        if (province) {
            setCityList(province.city);
        }
    };

    const handleClick = (e: React.MouseEvent, item: string, category: SelectTypeEnum) => {
        e.stopPropagation();

        const newValue = value.slice(0);

        switch (category) {
            case SelectTypeEnum.PROVINCE:
                onChange([item], false);
                getCity(provinceList, item);
                break;
            case SelectTypeEnum.CITY:
                onChange([newValue[0], item], false);
                break;
            case SelectTypeEnum.DISTRICT:
                newValue.splice(2, 1, item);
                onChange(newValue, true);
                break;
            default:
                break;
        }
    };

    const handleWrapper = (ref: HTMLDivElement) => {
        wrapperRef.current = ref;

        if (ref) {
            document.addEventListener('click', onCancel);
        } else {
            document.removeEventListener('click', onCancel);
        }
    };

    const handleProvinceRef = (ref: HTMLUListElement) => {
        if (ref) {
            provinceRef.current = ref;

            if (provinceRef.current && value[0]) {
                const index = provinceList.findIndex(item => item.province === value[0]);

                if (index >= 0) {
                    detectScroll(provinceRef.current, 'province-cascader-item', index);
                }
            }
        } else {
            provinceRef.current = null;
        }
    };

    const handleCityRef = (ref: HTMLUListElement) => {
        if (ref) {
            cityRef.current = ref;

            if (cityRef.current && value[1]) {
                const index = cityList.findIndex(item => item.city === value[1]);

                if (index >= 0) {
                    detectScroll(cityRef.current, 'province-cascader-item', index);
                }
            }
        } else {
            cityRef.current = null;
        }
    };

    const handleDistrictRef = (ref: HTMLUListElement) => {
        if (ref) {
            districtRef.current = ref;

            if (districtRef.current && value[2]) {
                const index = districtList.findIndex(item => item.district === value[2]);

                if (index >= 0) {
                    detectScroll(districtRef.current, 'province-cascader-item', index);
                }
            }
        } else {
            districtRef.current = null;
        }
    };

    useEffect(() => {
        if (cityRef.current) {
            const index = cityList.findIndex(item => item.city === value[1]);

            if (index < 0) {
                scrollToTop(cityRef.current, 0);
            }
        }
    }, [cityList, value]);

    useEffect(() => {
        if (districtRef.current) {
            const index = districtList.findIndex(item => item.district === value[2]);

            if (index < 0) {
                scrollToTop(districtRef.current, 0);
            }
        }
    }, [value, districtList]);

    const renderContent = () => {
        if (isLoading) {
            return <Skeleton line={8} />;
        }

        if (!dataProvince && error) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        return (
            <>
                {provinceList.length > 0 && (
                    <ul ref={handleProvinceRef} className="province-cascader-list">
                        {provinceList.map(({ province }) => (
                            <li
                                key={province}
                                title={province}
                                onClick={e => handleClick(e, province, SelectTypeEnum.PROVINCE)}
                                className={classList('province-cascader-item', {
                                    active: province === value[0]
                                })}>
                                {province}
                            </li>
                        ))}
                    </ul>
                )}
                {cityList.length > 0 && (
                    <ul ref={handleCityRef} className="province-cascader-list">
                        {cityList.map(({ city }) => (
                            <li
                                key={city}
                                title={city}
                                onClick={e => handleClick(e, city, SelectTypeEnum.CITY)}
                                className={classList('province-cascader-item', {
                                    active: city === value[1]
                                })}>
                                {city}
                            </li>
                        ))}
                    </ul>
                )}
                {districtList.length > 0 && (
                    <ul ref={handleDistrictRef} className="province-cascader-list">
                        {districtList.map(({ district }) => (
                            <li
                                key={district}
                                title={district}
                                onClick={e => handleClick(e, district, SelectTypeEnum.DISTRICT)}
                                className={classList('province-cascader-item', {
                                    active: district === value[2]
                                })}>
                                {district}
                            </li>
                        ))}
                    </ul>
                )}
            </>
        );
    };

    return (
        <div
            className={classList('province-cascader', {
                'is-top': isTop,
                'd-flex': !!dataProvince
            })}
            ref={handleWrapper}>
            {renderContent()}
        </div>
    );
};

export default ProvinceCascader;
